import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { request } from "../../lib/http";

type Query = Record<string, string | undefined>;

interface State<T> {
  data: T | null;
  error: string | null;
  /** Для каких deps получены data — по нему видно, что период сменили. */
  loadedKey: string | null;
  /** Идёт ли повторный запрос по таймеру или кнопке. */
  refreshing: boolean;
}

/**
 * Загрузка метрик с повтором по таймеру.
 *
 * Параметры собираются функцией в момент каждого запроса: у окна «последний
 * час» начало сдвигается вместе со временем, и посчитанное один раз оно
 * растягивалось бы с каждым обновлением. Перезагрузку сразу вызывает смена
 * deps — выбранного периода или ручки.
 *
 * Старые данные остаются на экране, пока идёт обновление: графики не должны
 * мигать пустотой раз в полминуты. Ответ на устаревший запрос (период успели
 * сменить) отбрасывается.
 */
export function useMetrics<T>(
  path: string,
  buildQuery: () => Query,
  deps: unknown[],
  refreshMs?: number,
) {
  const [state, setState] = useState<State<T>>({
    data: null,
    error: null,
    loadedKey: null,
    refreshing: false,
  });
  const key = JSON.stringify(deps);
  const generation = useRef(0);
  const builder = useRef(buildQuery);
  useLayoutEffect(() => {
    builder.current = buildQuery;
  });

  const fetchData = useCallback(
    async (forKey: string) => {
      const current = ++generation.current;
      try {
        const data = await request<T>(path, { query: builder.current() });
        if (current === generation.current) {
          setState({ data, error: null, loadedKey: forKey, refreshing: false });
        }
      } catch (err) {
        if (current === generation.current) {
          setState((prev) => ({
            ...prev,
            loadedKey: forKey,
            refreshing: false,
            error: err instanceof Error ? err.message : "не удалось загрузить метрики",
          }));
        }
      }
    },
    [path],
  );

  const refresh = useCallback(() => {
    setState((prev) => ({ ...prev, refreshing: true }));
    void fetchData(key);
  }, [fetchData, key]);

  useEffect(() => {
    void fetchData(key);
    if (!refreshMs) return;
    const timer = window.setInterval(refresh, refreshMs);
    return () => window.clearInterval(timer);
  }, [fetchData, key, refresh, refreshMs]);

  return {
    data: state.data,
    error: state.error,
    loading: state.loadedKey !== key || state.refreshing,
    reload: refresh,
  };
}
